import { Theme } from '@material-ui/core/styles';
import Color from 'color';
import WebFont from 'webfontloader';
import { PLAN_TYPES } from 'constants/paymentPlanConstants';
import { getTextColorForBackground, mixColors } from 'utils/general';
import { compact } from 'utils/standard';
import {
  ACTION_COLOR_OUTLINE_OPACITY,
  BACKGROUND_COLOR_DEEPER_OFFSET,
  BACKGROUND_COLOR_OFFSET,
  DEFAULT_GLOBAL_STYLE_CONFIG,
  DROP_SHADOW_OPACITY,
  INTERACTION_STATE_OPACITY,
  TEXT_SIZE_OFFSET_MAP,
  PLACEHOLDER_TEXT_OPACITY,
} from './constants';
import { GlobalStyleConfig, GlobalStyleTextConfig } from './types';

export const getFontFamilyName = (font: string): string => {
  // In case selected font is a custom font, removing extension
  return font.split('.')[0];
};

export const getBaseGlobalStyles = (config: GlobalStyleConfig | null): GlobalStyleConfig => {
  return config ?? DEFAULT_GLOBAL_STYLE_CONFIG;
};

export function getCategoricalColors(globalStyleConfig: GlobalStyleConfig) {
  const categoricalColors = globalStyleConfig.visualizations.categoricalPalette;
  return [
    categoricalColors.hue1,
    categoricalColors.hue2,
    categoricalColors.hue3,
    categoricalColors.hue4,
    categoricalColors.hue5,
    categoricalColors.hue6,
  ];
}

export function getDivergingColors(globalStyleConfig: GlobalStyleConfig) {
  const divergingColors = globalStyleConfig.visualizations.divergingPalette;
  const { hue1, hue2, hue3 } = divergingColors;

  return [
    hue1,
    mixColors(hue1, hue2, 0.66).hex().toString(),
    mixColors(hue1, hue2, 0.33).hex().toString(),
    hue2,
    mixColors(hue2, hue3, 0.66).hex().toString(),
    mixColors(hue2, hue3, 0.33).hex().toString(),
    hue3,
  ];
}

export function getGradientColors(globalStyleConfig: GlobalStyleConfig) {
  const gradientColors = globalStyleConfig.visualizations.gradientPalette;
  const { hue1, hue2 } = gradientColors;

  return [
    hue1,
    mixColors(hue1, hue2, 0.75).hex().toString(),
    mixColors(hue1, hue2, 0.5).hex().toString(),
    mixColors(hue1, hue2, 0.25).hex().toString(),
    hue2,
  ];
}

export function loadFonts(
  text: GlobalStyleTextConfig,
  teamId: number,
  teamPlan: PLAN_TYPES,
  isStylesPage = false,
) {
  if (!text) return;

  const fonts = compact([
    text.primaryFont,
    text.secondaryFont,
    text.overrides.h1?.font,
    text.overrides.h2?.font,
    text.overrides.kpiTitle?.font,
    text.overrides.kpiValue?.font,
    text.overrides.tableColumnHeader?.font,
    text.overrides.body?.font,
    text.overrides.smallHeading?.font,
    text.overrides.smallBody?.font,
  ]);

  const families: string[] = [];
  const urls: string[] = [];

  const customFonts: Set<string> = new Set();

  fonts.forEach((font) => {
    // If font has a period its a custom font with an extension in it. ex: CustomFont.otf
    if (font.includes('.')) {
      customFonts.add(font);
    } else {
      if (!isStylesPage && !families.includes(font)) {
        families.push(font);
        urls.push(
          `https://fonts.googleapis.com/css?family=${font.replace(' ', '+') || ''}&display=block`,
        );
      }
    }
  });

  const folder = process.env.REACT_APP_EXPORT_S3_FOLDER;
  const elementId = isStylesPage ? 'explo-custom-fonts-custom-styles' : 'explo-custom-fonts';
  if (folder && customFonts.size > 0) {
    let fontFaces = document.getElementById(elementId);
    if (fontFaces === null) {
      fontFaces = document.createElement('style');
      fontFaces.setAttribute('id', elementId);
    } else {
      fontFaces.innerHTML = '';
    }
    customFonts.forEach((font) => {
      const [fontId, fontExt] = font.split('.');

      let formatType;
      if (fontExt === 'ttf') formatType = 'truetype';
      else if (fontExt === 'otf') formatType = 'opentype';
      else formatType = fontExt;

      const fontUrl = `https://explo-custom-fonts.s3.us-west-1.amazonaws.com/${folder}/${teamId}/${font}`;
      const fontFace = `
      @font-face {
        font-family: "${fontId}";
        src: url("${fontUrl}") format("${formatType}");
      }
      `;
      (fontFaces as HTMLElement).appendChild(document.createTextNode(fontFace));
    });
    document.head.appendChild(fontFaces);
  }

  WebFont.load({ custom: { families, urls }, classes: false, events: false });
}

export function getCalculatedStyles(globalStyleConfig: GlobalStyleConfig, theme: Theme) {
  const {
    base: {
      actionColor: {
        default: defaultActionColor,
        buttonColor: maybeButtonColor,
        interactionStateColor: maybeInteractionStateColor,
      },
      backgroundColor: baseBackgroundColor,
    },
    container: {
      fill: containerFillColor,
      outline: {
        enabled: containerOutlineEnabled,
        color: containerOutlineColor,
        weight: containerOutlineWeight,
      },
      shadow: {
        enabled: containerShadowEnabled,
        color: containerShadowColor,
        size: containerShadowSize,
      },
      cornerRadius: {
        default: defaultContainerCornerRadius,
        inputFields: maybeInputFieldCornerRadius,
      },
    },
    text: { secondaryColor: secondaryTextColor },
  } = globalStyleConfig;

  const backgroundColorObject = new Color(baseBackgroundColor);
  const { white, black } = theme.palette.ds;
  const buttonColor = maybeButtonColor ?? defaultActionColor;
  const interactionStateColor = new Color(maybeInteractionStateColor ?? defaultActionColor)
    // We want interaction states to have 10% opacity
    .fade(
      backgroundColorObject.isDark() ? INTERACTION_STATE_OPACITY : 1 - INTERACTION_STATE_OPACITY,
    )
    .rgb()
    .string();
  const actionColorOutline = new Color(defaultActionColor)
    .fade(1 - ACTION_COLOR_OUTLINE_OPACITY)
    .rgb();
  const containerFillColorObject = new Color(containerFillColor);
  const dropShadowColor = new Color(containerShadowColor)
    .fade(1 - DROP_SHADOW_OPACITY)
    .rgb()
    .string();
  const placeholderTextColor = new Color(secondaryTextColor)
    .fade(1 - PLACEHOLDER_TEXT_OPACITY)
    .rgb()
    .string();
  const inputFieldCornerRadius = maybeInputFieldCornerRadius ?? defaultContainerCornerRadius;
  const outlineBoxShadowBase = `0 0 0 ${containerOutlineWeight}px ${containerOutlineColor}`;

  const getButtonStyles = (backgroundColor: string, alpha: number) => {
    const mixedColor = mixColors(backgroundColor, white, alpha).rgb().string();
    return {
      backgroundColor: `${mixedColor} !important`,
      color: getTextColorForBackground(mixedColor, theme),
    };
  };

  const getMinimalButtonStyles = (backgroundColor: string, alpha: number) => {
    const mixedColor = mixColors(backgroundColor, white, alpha).rgb().string();
    return {
      backgroundColor: mixedColor,
      color: secondaryTextColor,
    };
  };

  const getTextSize = (type: keyof GlobalStyleTextConfig['overrides']) => {
    return (
      globalStyleConfig.text.overrides[type]?.size ||
      globalStyleConfig.text.textSize + TEXT_SIZE_OFFSET_MAP[type]
    );
  };
  const getTextFont = (type: keyof GlobalStyleTextConfig['overrides'], secondary?: boolean) => {
    const font =
      globalStyleConfig.text.overrides[type]?.font ||
      globalStyleConfig.text[secondary ? 'secondaryFont' : 'primaryFont'];
    return font ? getFontFamilyName(font) : undefined;
  };
  const getTextColor = (type: keyof GlobalStyleTextConfig['overrides'], secondary?: boolean) => {
    return (
      globalStyleConfig.text.overrides[type]?.color ||
      globalStyleConfig.text[secondary ? 'secondaryColor' : 'primaryColor']
    );
  };
  const getTextStyles = (
    type: keyof GlobalStyleTextConfig['overrides'],
    options: { color?: boolean; secondary?: boolean } = {},
  ) => {
    const { color = true, secondary } = options;
    const fontFamily = getTextFont(type, secondary);

    return {
      fontSize: getTextSize(type),
      ...(fontFamily && { fontFamily }),
      ...(color && { color: getTextColor(type, secondary) }),
    };
  };

  return {
    base: {
      backgroundColorStyle: {
        backgroundColorStyle: {
          backgroundColor: baseBackgroundColor,
        },
      },
      actionColor: {
        default: {
          backgroundColorStyle: {
            backgroundColor: defaultActionColor,
          },
          blackOrWhiteColorStyle: {
            // TODO PD-1260 we should get rid of the important
            color: `${getTextColorForBackground(defaultActionColor, theme)} !important`,
          },
          boxShadowStyle: {
            // rgb(16 22 26 / 20%) part comes directly from Blueprint
            boxShadow: `0 0 0 1px ${defaultActionColor}, 0 0 0 3px ${actionColorOutline}, inset 0 1px 1px rgb(16 22 26 / 20%)`,
          },
          boxShadowImportantStyle: {
            boxShadow: `0 0 0 1px ${defaultActionColor}, 0 0 0 3px ${actionColorOutline}, inset 0 1px 1px rgb(16 22 26 / 20%) !important`,
          },
          borderColorStyle: {
            borderColor: defaultActionColor,
          },
        },
        buttonColor: {
          colorStyle: {
            color: buttonColor,
          },
          backgroundColorStyle: {
            backgroundColor: buttonColor,
          },
          buttonMinimalColor: {
            backgroundColor: 'transparent',
            color: secondaryTextColor,
          },
          buttonMinimalHoverStyle: getMinimalButtonStyles(buttonColor, 0.92),
          buttonMinimalActiveStyle: getMinimalButtonStyles(buttonColor, 0.84),
          buttonMinimalActiveHoverStyle: getMinimalButtonStyles(buttonColor, 0.76),
          blackOrWhiteColorStyle: {
            color: getTextColorForBackground(buttonColor, theme),
          },
          buttonHoverStyle: getButtonStyles(buttonColor, 0.92),
          buttonActiveStyle: getButtonStyles(buttonColor, 0.84),
          buttonActiveHoverStyle: getButtonStyles(buttonColor, 0.76),
        },
        interactionStateColor: {
          borderColorStyle: {
            borderColor: interactionStateColor,
          },
          boxShadowStyle: {
            boxShadow: `inset 0 0 0 1px ${interactionStateColor}`,
          },
        },
      },
    },
    container: {
      fill: {
        offsetBackgroundColorStyle: {
          backgroundColor: mixColors(
            containerFillColor,
            containerFillColorObject.isDark() ? white : black,
            1 - BACKGROUND_COLOR_OFFSET,
          )
            .rgb()
            .toString(),
        },
        deeperOffsetBackgroundColorStyle: {
          backgroundColor: mixColors(
            containerFillColor,
            containerFillColorObject.isDark() ? white : black,
            1 - BACKGROUND_COLOR_DEEPER_OFFSET,
          )
            .rgb()
            .toString(),
        },
      },
      outline: {
        borderStyle: {
          border: containerOutlineEnabled
            ? `${containerOutlineWeight}px solid ${containerOutlineColor}`
            : '1px solid transparent',
        },
        boxShadowStyle: {
          boxShadow: containerOutlineEnabled ? outlineBoxShadowBase : 'none',
        },
        boxShadowInsetStyle: {
          boxShadow: containerOutlineEnabled ? `inset ${outlineBoxShadowBase}` : 'none',
        },
      },
      shadow: {
        shadowStyle: {
          boxShadow:
            containerShadowEnabled && containerShadowSize
              ? `${containerShadowSize}px ${containerShadowSize}px ${
                  containerShadowSize * 2
                }px ${dropShadowColor}`
              : 'none',
        },
      },
      cornerRadius: {
        default: {
          borderRadiusStyle: {
            borderRadius: defaultContainerCornerRadius,
          },
        },
        inputFieldCornerRadius: {
          borderRadiusStyle: {
            borderRadius: inputFieldCornerRadius,
          },
        },
      },
    },
    text: {
      secondaryColor: {
        inputPlaceholderStyle: {
          color: placeholderTextColor,
        },
      },
      overrides: {
        body: {
          primaryStyle: {
            ...getTextStyles('body'),
          },
          secondaryColorStyle: {
            color: getTextColor('body', true),
          },
        },
      },
    },
  };
}

export const getWhiteOrBlack = (color: string | Color): string => {
  if (typeof color === 'string') color = new Color(color);
  return color.isDark() ? 'white' : 'black';
};
